/**
 * Portfolio's — klant → portfolio → client lead. Vier portfolio's (energie, bureaus, publiek/zorg/
 * dienstverlening, industrie/retail). De portfolio wordt afgeleid uit de branche (en Very Video-lijst)
 * en kan per klant worden overschreven; de client lead per portfolio staat in de instelling
 * `portfolio_leads` en kan per klant worden overschreven. Voedt het portfolio-filter in de Opdracht-mix.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';

const euro = (v) => `€ ${Math.round(v || 0).toLocaleString('nl-NL')}`;

export default function Portfolios() {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState('');
  const [leads, setLeads] = useState({});
  const [filter, setFilter] = useState('alle');   // alle | <portfolio> | leeg
  const [zoek, setZoek] = useState('');
  const [bezig, setBezig] = useState(false);

  const laad = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/gripp/portfolios`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Laden mislukt');
      setData(d); setLeads(d.leads || {}); setFout('');
    } catch (e) { setFout(e.message); }
  }, []);
  useEffect(() => { laad(); }, [laad]);

  const leadsDirty = useMemo(() => data && JSON.stringify(leads) !== JSON.stringify(data.leads || {}), [leads, data]);
  const bewaarLeads = async () => {
    setBezig(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/gripp/portfolios/leads`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ leads }) });
      if (!r.ok) throw new Error('Opslaan mislukt');
      toast.success('Client leads opgeslagen'); await laad();
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  const zetKlant = async (k, patch) => {
    const body = { company_name: k.company, portfolio: k.portfolio_bron === 'handmatig' ? k.portfolio : null, client_lead: k.client_lead_bron === 'handmatig' ? k.client_lead : null, ...patch };
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/gripp/portfolios/klant/${k.company_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error('Opslaan mislukt');
      await laad();
    } catch (e) { toast.error(e.message); }
  };

  const labelVan = (key) => data?.portfolios?.find((p) => p.key === key)?.label || key || '–';
  const klanten = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    return (data?.klanten || []).filter((k) => (filter === 'alle' || (filter === 'leeg' ? !k.portfolio : k.portfolio === filter)) && (!q || k.company.toLowerCase().includes(q)));
  }, [data, filter, zoek]);
  const totaalPer = useMemo(() => {
    const m = {};
    for (const k of data?.klanten || []) { const key = k.portfolio || 'leeg'; m[key] = (m[key] || 0) + k.omzet; }
    return m;
  }, [data]);

  return (
    <Box>
      <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 0.5 }}>🗂️ Portfolio's en client leads</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        Elke klant valt in één portfolio, afgeleid uit de branche (Very Video-bureaus vallen altijd onder Bureaus). Overschrijf per klant waar
        de afleiding niet klopt. De client lead geldt per portfolio en kan per klant afwijken. De Opdracht-mix filtert hierop, zodat een
        lead alleen het eigen portfolio ziet.
      </Typography>
      {fout && <Alert severity="error" sx={{ mb: 2 }}>{fout}</Alert>}
      {!data ? <Typography>Laden…</Typography> : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
            {data.portfolios.map((p) => (
              <Box key={p.key} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>{p.label}</Typography>
                <Typography sx={{ fontSize: 12, color: '#94a3b8', mb: 1 }}>{(data.klanten || []).filter((k) => k.portfolio === p.key).length} klanten · {euro(totaalPer[p.key])} sinds 2025</Typography>
                <TextField size="small" fullWidth label="Client lead" value={leads[p.key] || ''} onChange={(e) => setLeads((prev) => ({ ...prev, [p.key]: e.target.value }))} />
              </Box>
            ))}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Button variant="contained" disableElevation disabled={!leadsDirty || bezig} onClick={bewaarLeads} sx={{ textTransform: 'none', fontWeight: 700 }}>
              {bezig ? 'Opslaan…' : 'Client leads opslaan'}
            </Button>
            {leadsDirty && <Typography variant="caption" color="text.secondary">Nog niet opgeslagen.</Typography>}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_e, v) => v && setFilter(v)}
              sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: 12, textTransform: 'none', fontWeight: 700 } }}>
              <ToggleButton value="alle">Alle</ToggleButton>
              {data.portfolios.map((p) => <ToggleButton key={p.key} value={p.key}>{p.label.replace(' (Very Video)', '')}</ToggleButton>)}
              <ToggleButton value="leeg">Niet ingedeeld ({(data.klanten || []).filter((k) => !k.portfolio).length})</ToggleButton>
            </ToggleButtonGroup>
            <TextField size="small" placeholder="Zoek klant…" value={zoek} onChange={(e) => setZoek(e.target.value)} sx={{ minWidth: 220 }} />
          </Stack>
          <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Klant</TableCell>
                  <TableCell>Branche</TableCell>
                  <TableCell align="right">Omzet 2025+</TableCell>
                  <TableCell>Portfolio</TableCell>
                  <TableCell>Client lead</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {klanten.map((k) => (
                  <TableRow key={k.company_id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {k.company}
                      {k.exclusive && <Chip size="small" label={k.exclusive} sx={{ ml: 0.75, height: 18, fontSize: 10, bgcolor: '#fce7f3', color: '#9d174d' }} />}
                    </TableCell>
                    <TableCell sx={{ color: '#64748b', fontSize: 12.5 }}>{k.branche || '–'}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{euro(k.omzet)} <Box component="span" sx={{ color: '#94a3b8', fontSize: 11 }}>· {k.n}×</Box></TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>{k.portfolio_bron === 'handmatig' ? 'Handmatig' : 'Afgeleid'}</InputLabel>
                        <Select label={k.portfolio_bron === 'handmatig' ? 'Handmatig' : 'Afgeleid'} value={k.portfolio || ''}
                          onChange={(e) => zetKlant(k, { portfolio: e.target.value || null })}
                          sx={{ fontSize: 13, bgcolor: k.portfolio_bron === 'handmatig' ? '#fef3c7' : '#fff' }}>
                          <MenuItem value="">(volg branche)</MenuItem>
                          {data.portfolios.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <TextField size="small" fullWidth placeholder={k.portfolio ? (leads[k.portfolio] || 'geen lead') : '–'}
                        defaultValue={k.client_lead_bron === 'handmatig' ? k.client_lead : ''}
                        key={`${k.company_id}-${k.client_lead_bron}-${k.client_lead || ''}`}
                        onBlur={(e) => { const v = e.target.value.trim(); if (v !== (k.client_lead_bron === 'handmatig' ? k.client_lead : '')) zetKlant(k, { client_lead: v || null }); }}
                        sx={{ '& .MuiInputBase-root': { fontSize: 13, bgcolor: k.client_lead_bron === 'handmatig' ? '#fef3c7' : '#fff' } }} />
                    </TableCell>
                  </TableRow>
                ))}
                {klanten.length === 0 && <TableRow><TableCell colSpan={5} sx={{ color: '#94a3b8' }}>Geen klanten in deze selectie</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Geel = handmatig overschreven. Afleiding: {Object.entries(data.branche_naar_portfolio || {}).map(([b, p]) => `${b} → ${labelVan(p).replace(' (Very Video)', '')}`).join(' · ')}.
          </Typography>
        </>
      )}
    </Box>
  );
}
